import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getGoogleCredential, loginUser, oauthLogin, storeAuth } from '../../lib/auth';

const oauthProviders = ['Google', 'Microsoft', 'ORCID'];

function Login() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Sign In - JAMSD';
  }, []);

  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthModal, setOauthModal] = useState({ open: false, provider: '', email: '' });

  const validate = () => {
    const newErrors = {};
    if (!form.email.trim()) newErrors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = 'Please enter a valid email address.';
    if (!form.password) newErrors.password = 'Password is required.';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const auth = await loginUser(form);
      storeAuth(auth);
      navigate('/');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider) => {
    setLoading(true);
    setMessage('');
    try {
      let auth;
      if (provider === 'Google') {
        const idToken = await getGoogleCredential();
        if (idToken) {
          auth = await oauthLogin('google', { idToken });
        }
      }

      if (!auth) {
        setOauthModal({ open: true, provider, email: '' });
        return;
      }

      storeAuth(auth);
      navigate('/');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOAuthEmail = async (event) => {
    event.preventDefault();
    const email = oauthModal.email.trim();
    if (!email) {
      setMessage('Email is required to continue.');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const auth = await oauthLogin(oauthModal.provider.toLowerCase(), {
        email,
        fullName: email.split('@')[0],
        providerId: email,
      });
      storeAuth(auth);
      navigate('/');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
      setOauthModal({ open: false, provider: '', email: '' });
    }
  };

  const inputClass = (field) =>
    `w-full font-sans text-sm border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent transition-colors ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-bg-border bg-white'
    }`;

  return (
    <>
    <div className="min-h-screen bg-bg-cool pt-24 pb-16 flex items-start justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-bg-border shadow-lg overflow-hidden">
          <div className="bg-navy px-8 py-7 text-center">
            <Link to="/" className="inline-flex items-center gap-2 justify-center mb-4">
              <div className="w-1 h-8 bg-amber rounded-full" aria-hidden="true" />
              <span className="font-display font-bold text-amber text-xl">JAMSD</span>
            </Link>
            <h1 className="font-display text-white text-2xl font-bold">Sign In</h1>
            <p className="font-sans text-slate-light text-sm mt-1">Access your JAMSD account</p>
          </div>

          <div className="px-8 py-8">
            {message && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700">
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label htmlFor="email" className="block font-sans text-sm font-semibold text-navy mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={inputClass('email')}
                  autoComplete="email"
                />
                {errors.email && <p className="font-sans text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="password" className="font-sans text-sm font-semibold text-navy block mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={inputClass('password')}
                  autoComplete="current-password"
                />
                {errors.password && <p className="font-sans text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <button type="submit" disabled={loading} className="btn-amber w-full py-3 text-base disabled:opacity-60">
                {loading ? 'Signing In...' : 'Sign In'}
              </button>

              {/* <div className="grid grid-cols-1 gap-2">
                {oauthProviders.map((provider) => (
                  <button
                    key={provider}
                    type="button"
                    disabled={loading}
                    onClick={() => handleOAuth(provider)}
                    className="w-full rounded border border-bg-border bg-white px-4 py-2.5 font-sans text-sm font-semibold text-navy hover:border-amber hover:text-amber transition-colors disabled:opacity-60"
                  >
                    Continue with {provider}
                  </button>
                ))}
              </div> */}

              <p className="font-sans text-center text-sm text-slate">
                Don't have an account?{' '}
                <Link to="/auth/register" className="text-amber font-semibold hover:text-amber-dark transition-colors">
                  Create account
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
    {oauthModal.open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
        <form onSubmit={submitOAuthEmail} className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
          <h2 className="font-display text-xl font-bold text-navy">Continue with {oauthModal.provider}</h2>
          <label className="mt-4 block font-sans text-sm font-semibold text-navy">
            Account Email
            <input
              type="email"
              value={oauthModal.email}
              onChange={(event) => setOauthModal((prev) => ({ ...prev, email: event.target.value }))}
              className="mt-1 w-full rounded border border-bg-border px-3 py-2"
              autoFocus
            />
          </label>
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" onClick={() => setOauthModal({ open: false, provider: '', email: '' })} className="rounded border border-bg-border px-4 py-2 text-sm font-semibold text-navy">
              Cancel
            </button>
            <button type="submit" className="btn-amber px-5 py-2" disabled={loading}>
              Continue
            </button>
          </div>
        </form>
      </div>
    )}
    </>
  );
}

export default Login;
